import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  type TableCellProps,
} from '@mui/material';
import { flexRender, type Table as TTable } from '@tanstack/react-table';

import { GridWrapper } from '../styled';
import NoData from 'components/no-data';
import ArrowIcon from 'components/icons/arrow-icon';
import SortIcon from 'components/icons/sort-icon';

import Row, { RowProps } from './Row';

function alignColumn(align: TableCellProps['align'] = 'left') {
  switch (align) {
    case 'center':
      return 'center';
    case 'right':
      return 'flex-end';
  }
}

function capitalize(str: string) {
  const lowercase = str.toLocaleLowerCase();

  return `${lowercase[0].toUpperCase()}${lowercase.slice(1)}`;
}

type GridProps<T extends object> = {
  table: TTable<T>;
  isLoading?: boolean;
  noDataText?: string;
} & Pick<RowProps<T>, 'onClickRow'>;

const Grid = <T extends object>({
  table,
  isLoading,
  noDataText,
  onClickRow,
}: GridProps<T>) => {
  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;

  const showNoDataComponent = !rows.length && !isLoading;

  return (
    <>
      <GridWrapper>
        <Table>
          <TableHead>
            {headerGroups.map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const reactNode = flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  );

                  return (
                    <TableCell
                      key={header.id}
                      sx={{
                        verticalAlign: 'top',
                        ...header.column.columnDef?.meta?.sx,
                      }}
                    >
                      <Stack
                        alignItems="center"
                        justifyContent={alignColumn(
                          header.column.columnDef?.meta?.align
                        )}
                        direction="row"
                        spacing={1}
                      >
                        <Stack
                          alignItems="center"
                          direction="row"
                          spacing={1}
                          sx={{
                            whiteSpace: 'nowrap',
                            ...(header.column.getCanSort() && {
                              cursor: 'pointer',
                              '&:hover': {
                                color: 'rgba(0, 0, 0, 0.54)',
                              },
                            }),
                          }}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {typeof reactNode === 'string'
                            ? capitalize(reactNode)
                            : reactNode}
                          {header.column.getCanSort()
                            ? {
                                asc: <ArrowIcon dir="top" />,
                                desc: <ArrowIcon dir="bottom" />,
                              }[header.column.getIsSorted() as string] ?? (
                                <SortIcon style={{ fontSize: '14px' }} />
                              )
                            : null}
                        </Stack>
                        {header.column.getCanFilter() ? (
                          header.column.columnDef?.meta &&
                          header.column.columnDef?.meta.filterComponent ? (
                            <div>
                              {header.column.columnDef?.meta.filterComponent({
                                column: header.column,
                                table,
                              })}
                            </div>
                          ) : null
                        ) : null}
                      </Stack>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {!isLoading &&
              rows.map((row) => (
                <Row key={row.id} row={row} onClickRow={onClickRow} />
              ))}
          </TableBody>
        </Table>
      </GridWrapper>

      {isLoading && (
        <CircularProgress
          color="primary"
          size={30}
          sx={{
            display: 'block',
            alignSelf: 'center',
            margin: '0 auto',
            marginTop: '10px',
          }}
        />
      )}
      {showNoDataComponent && <NoData text={noDataText} />}
    </>
  );
};

export default Grid;
